import GlobalSearchGroup from "@/components/GlobalSearchGroup";
import Column from "@/components/Ui/Column";
import Icon from "@/components/Ui/Icon";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/shadcn/input-group";
import { Spinner } from "@/components/shadcn/spinner";
import { useSesamyAuthTokenContext } from "@/contexts/sesamy-auth-token-context";
import useOnClickOutside from "@/hooks/use-click-outside";
import { GlobalSearchResult } from "@/types/GlobalSearch";
import { fetchSearchResults } from "@/utils/global-search";
import { captureException } from "@/utils/tracking";
import { Search } from "lucide-react";
import { ChangeEvent, FocusEvent, useRef, useState } from "react";

let searchTimeout = setTimeout(() => {});

const GlobalSearch = () => {
  const { accessToken } = useSesamyAuthTokenContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<GlobalSearchResult>();
  const [isSearching, setIsSearching] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const doSearch = async (query: string) => {
    if (!query || !accessToken) return;
    setIsSearching(true);
    try {
      const results = await fetchSearchResults({
        query,
        accessToken,
        limit: 5,
      });
      setSearchResults(results);
    } catch (error) {
      captureException(error, { searchContext: "search failed" });
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setSearchResults(undefined);
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => doSearch(query), 500);
  };
  const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const handleInputClear = () => {
    setSearchQuery("");
    setSearchResults(undefined);
  };

  useOnClickOutside(wrapperRef, () => {
    if (searchQuery) {
      setSearchQuery("");
    }
  });

  return (
    <div className="relative z-10 w-[300px]" ref={wrapperRef}>
      <InputGroup>
        <InputGroupInput
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder="Search for users, contracts, bills"
          type="search"
        />
        <InputGroupAddon>
          {isSearching ? (
            <Spinner className="text-primary animate-spin" />
          ) : (
            <Search />
          )}
        </InputGroupAddon>
      </InputGroup>

      {searchQuery && (
        <button
          className="absolute top-1/2 right-3 flex -translate-y-1/2 cursor-default p-2"
          onClick={handleInputClear}
        >
          <Icon name="cross" className="h-2.5 w-2.5" />
        </button>
      )}
      {searchQuery && searchResults && (
        <div className="bg-card absolute top-full right-0 -left-[400px] mt-2 overflow-hidden rounded text-white shadow-lg">
          <div className="custom-scrollbar custom-inline-scrollbar max-h-[calc(100vh-80px)] overflow-y-scroll overscroll-none">
            {!isSearching && (
              <Column left className="gap-5 py-4 pr-1 pl-5">
                {searchResults && Object.keys(searchResults).length === 0 && (
                  <Column className="w-full gap-2 py-5 text-sm">
                    <div className="font-medium">No search results found</div>
                    <div>Please try again with a different search query</div>
                  </Column>
                )}
                {searchResults &&
                  Object.keys(searchResults).length > 0 &&
                  Object.keys(searchResults).map((key, i) => (
                    <GlobalSearchGroup
                      key={key}
                      index={i}
                      title={key}
                      searchQuery={searchQuery}
                      data={searchResults[key].data}
                      totalDocuments={searchResults[key].totalDocuments}
                    />
                  ))}
              </Column>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
